// src/components/apps/Licenses/LicensesForm.js
import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Grid, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  FormControlLabel, Switch
} from '@mui/material';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useAuth } from '../../../context/AuthContext';

const initialFormState = {
  date_received: '',
  date_due: '',
  site: '',
  site_poc: '',
  site_poc_email: '',
  category: '',
  department: '',
  software: '',
  version: '',        // ✅ added
  description: '',    // ✅ added
  vendor: '',
  vendor_poc: '',
  vendor_quote: '',
  vendor_poc_email: '',
  total: '',
  gl_account: '',
  status: '',
  renewed_date: '',
  notes: '',
  // NEW FIELDS
  version: '',              // free text
  webapp: false,            // boolean
  ready_to_install: false,  // boolean
  packaged: false,          // boolean
};

const siteOptions = ['Division', 'Baltimore', 'Erlanger', 'Cincinnati', 'Rockmart', 'Saltillo', 'San Diego'];
const statusOptions = ['Quoting', 'Processing', 'Purchased', 'Complete', 'Not Started', 'Other'];

const labelize = str =>
  str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const LicensesForm = ({ selectedLicense = null, onSuccess }) => {
  const { apiFetch } = useAuth();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (selectedLicense) {
      // merge defaults to ensure newly added fields exist during edit
      setFormData({ ...initialFormState, ...selectedLicense });
      setEditing(true);
    } else {
      setFormData(initialFormState);
      setEditing(false);
    }
  }, [selectedLicense]);

  const isEmail = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);

  const validateField = (field, value) => {
    switch (field) {
      case 'site':
        return value ? '' : 'This field is required';
      case 'site_poc_email':
      case 'vendor_poc_email':
        if (!value) return ''; // optional
        return isEmail(value) ? '' : 'Invalid email';
      case 'total':
        return !value || isNaN(value) ? 'Enter a valid number' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleToggle = (name) => (e) => {
    const { checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: checked };
      // If webapp is true, force the others off to mirror your rule
      if (name === 'webapp' && checked) {
        next.ready_to_install = false;
        next.packaged = false;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    // Validate only text/number/date fields
    const fieldsToValidate = [
      'site','site_poc_email','vendor_poc_email','total'
    ];
    fieldsToValidate.forEach((key) => {
      const err = validateField(key, formData[key]);
      if (err) newErrors[key] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const url = editing
      ? API_ENDPOINTS.SOFTWARE_LICENSE_DETAIL(formData.id)
      : API_ENDPOINTS.SOFTWARE_LICENSES;
    const method = editing ? 'PUT' : 'POST';

    const formattedData = {
      ...formData,
      date_due: formData.date_due ? String(formData.date_due).slice(0, 10) : null,
      renewed_date: formData.renewed_date ? String(formData.renewed_date).slice(0, 10) : null,
      date_received: formData.date_received ? String(formData.date_received).slice(0, 10) : null,
      total: formData.total === '' ? null : formData.total,
    };

    try {
      const resp = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to save app.');
      }
      alert(editing ? 'App updated' : 'App added');
      onSuccess && onSuccess();
    } catch (err) {
      console.error('Submit error:', err);
      alert(err.message || 'Failed to save app.');
    }
  };

  const handleDeleteConfirmed = async () => {
    try {
      const resp = await apiFetch(API_ENDPOINTS.SOFTWARE_LICENSE_DETAIL(formData.id), {
        method: 'DELETE',
      });
      if (!resp.ok) {
        throw new Error('Failed to delete license.');
      }
      alert('License deleted');
      setConfirmOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete license.');
      setConfirmOpen(false);
    }
  };

  // Exclude the boolean fields from the generic text fields loop
  const textFieldOrder = [
    'software',
    'version',          // <-- keep version as a normal text field
    'category',
    'department',
    'vendor',
    'vendor_poc',
    'vendor_poc_email',
    'vendor_quote',
    'gl_account',
    'status',
    'site_poc',
    'site_poc_email',
    'total',
    'notes',
    'description',
    'date_received',
    'date_due',
    'renewed_date',
    // 'site' is rendered as a select below
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
      <Grid container spacing={2}>

        {/* Site select */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Site"
            name="site"
            value={formData.site}
            onChange={handleChange}
            error={Boolean(errors.site)}
            helperText={errors.site || ''}
          >
            {siteOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Status select */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Generic text/date/number fields */}
        {textFieldOrder.map((field) => (
          <Grid item xs={12} sm={6} key={field}>
            <TextField
              fullWidth
              multiline={field === 'notes' || field === 'description'}
              label={labelize(field)}
              name={field}
              type={
                field.includes('date') ? 'date' :
                field === 'total' ? 'number' : 'text'
              }
              InputLabelProps={field.includes('date') ? { shrink: true } : undefined}
              inputProps={field === 'total' ? { inputMode: 'decimal', step: '0.01' } : {}}
              value={formData[field] ?? ''}
              onChange={handleChange}
              error={Boolean(errors[field])}
              helperText={errors[field] || ''}
            />
          </Grid>
        ))}

        {/* Boolean toggles */}
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.webapp}
                onChange={handleToggle('webapp')}
              />
            }
            label="Web App"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.ready_to_install}
                onChange={handleToggle('ready_to_install')}
                disabled={!!formData.webapp} // disabled when webapp is true
              />
            }
            label="Ready to Install"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.packaged}
                onChange={handleToggle('packaged')}
                disabled={!!formData.webapp} // disabled when webapp is true
              />
            }
            label="Packaged"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {editing && (
              <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
                Delete
              </Button>
            )}
            <Button type="submit" variant="contained" color="primary">
              {editing ? 'Update' : 'Add'} App
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this license? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicensesForm;